import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { Bell, AlertCircle, Clock, DollarSign, UserPlus, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SEVERITY_ICON = {
  error: AlertCircle,
  warning: Clock,
  info: DollarSign,
};

const TYPE_ICON = {
  pending_signup: UserPlus,
  plan_expiring: Clock,
  due_soon: AlertCircle,
};

export default function NotificationsBell({ onOpenClient, onOpenAdmin }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const count = items.length;

  const dismiss = async (notificationId) => {
    try {
      await api.delete(`/notifications/${encodeURIComponent(notificationId)}`);
      setItems((current) => current.filter((item) => item.id !== notificationId));
    } catch {
      // Keep the notification visible if the server could not dismiss it.
    }
  };

  const handleClick = (n) => {
    setOpen(false);
    if (n.action_url) {
      navigate(n.action_url);
    } else if (n.type === "pending_signup" && onOpenAdmin) {
      onOpenAdmin();
    } else if (n.client_id && onOpenClient) {
      onOpenClient(n.client_id);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="relative inline-flex items-center justify-center w-10 h-10 rounded-full border border-slate-800 bg-slate-900/60 hover:bg-slate-800 text-slate-200 transition-colors"
          aria-label="Notificações"
          data-testid="notifications-bell"
        >
          <Bell className="w-4 h-4" />
          {count > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#060A12]"
              data-testid="notifications-count"
            >
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[min(360px,90vw)] bg-slate-900 border-slate-700 text-slate-100 p-0"
      >
        <div className="p-3 border-b border-slate-800 flex items-center justify-between">
          <div className="font-bold text-sm">Notificações</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">
            {count} nova(s)
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="py-8 text-center text-slate-500 text-sm">
              <Bell className="w-5 h-5 mx-auto mb-2 opacity-30" />
              Nada por aqui.
            </div>
          ) : (
            items.map((n) => {
              const Icon = TYPE_ICON[n.type] || SEVERITY_ICON[n.severity] || Bell;
              const color =
                n.severity === "error"
                  ? "text-rose-300"
                  : n.severity === "warning"
                    ? "text-amber-300"
                    : "text-blue-300";
              return (
                <div
                  key={n.id}
                  className="w-full text-left px-3 py-2.5 border-b border-slate-800/60 hover:bg-slate-800/60 transition-colors flex items-start gap-2.5"
                  data-testid={`notif-${n.id}`}
                >
                  <button type="button" onClick={() => handleClick(n)} className="flex min-w-0 flex-1 items-start gap-2.5 text-left">
                    <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${color}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-100 font-bold truncate">{n.title}</div>
                      {n.body && <div className="text-xs text-slate-400 truncate">{n.body}</div>}
                    </div>
                  </button>
                  <button type="button" onClick={() => dismiss(n.id)} aria-label="Limpar notificação" title="Limpar notificação" className="shrink-0 rounded p-1 text-slate-400 hover:text-white hover:bg-slate-700">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
